/**
 * Piezo Client Utilities for browser-side Piezo bundle assembly.
 * - Removed lodash dependency (uniq replaced with Set-based dedup)
 * - Removed approval path parsing (no Consensus/MPA flow)
 */

import { ParsePlenumDocumentCommandOutput } from '@amzn/piezo-plenum-types';
import {
  createInlineKeyPair,
  PiezoPlenumClient,
} from '@amzn/piezo-plenum-typescript';
import {
  bytesToEncoded,
  PermitInfo,
  PiezoNotaryThickClient,
  PiezoOkapiThickClient,
  PiezoWitnessThickClient,
} from '@amzn/piezo-typescript-thick-client';
import canonicalize from 'canonicalize';
import {
  PIEZO_CLERK_FAMILIES,
  PIEZO_DEFAULT_POLICY_TIER,
  PIEZO_MINIMUM_REQUIRED_ISSUERS,
  PIEZO_MINIMUM_UNIQUE_PERMITS_REQUIREMENT,
  PIEZO_POLICY_TIERS,
  PIEZO_POSIX_TAG_PREFIX,
  PIEZO_PRINCIPAL_POWER_RANK,
  PIEZO_TRIM_PERMITS_PER_POLICY,
  PIEZO_UNKNOWN_PRINCIPAL_RANK,
  PiezoDebugPrefix,
  PiezoIndexedDBMetadata,
  PiezoKeyPairAlgorithm,
  PiezoProtocolVersion,
} from './PiezoClient.constants';
import {
  PiezoCreateActionInfoDocInput,
  PiezoCreateArtifactBundleInput,
  PiezoDecks,
  PiezoEncodedNotaryDecks,
  PiezoFetchedNotaryDecks,
  PiezoFetchedPermitsMap,
  PiezoFetchedWitnessDeckbox,
  PiezoIndexedDBSchema,
  PiezoNotaryDecks,
  PiezoOperatorRoles,
  PiezoParsedPermitMetadata,
  PiezoParsedPolicyMetadata,
  PiezoPolicyIndex,
  PiezoPolicyResolution,
  PiezoToolIndex,
  PiezoWitnessDeckbox,
} from './PiezoClient.types';

// ── General Utils ──────────────────────────────────────────────────────────────

/**
 * Initializes the Piezo SDK clients Plenum, Okapi, Notary, and Witness.
 * Uses Midway auth (operator's browser session).
 *
 * Witness is partitional; the region passed here is the deployment region, and
 * PiezoWitnessThickClient internally maps it to a valid Witness region via
 * selectWitnessRegion and hedges across all Witness regions.
 */
export const initPiezoSDKClients = async (
  region: string,
): Promise<{
  plenumClient: PiezoPlenumClient;
  okapiClient: PiezoOkapiThickClient;
  notaryClient: PiezoNotaryThickClient;
  witnessClient: PiezoWitnessThickClient;
}> => {
  // By far the longest: instantiates the WASM, and compiles it too unless adoptSharedPiezoWasm ran.
  const plenumClient = await PiezoPlenumClient.initialize(undefined);
  if (!plenumClient) {
    throw new Error('Failed to initialize Plenum client');
  }

  const okapiClient = new PiezoOkapiThickClient(globalThis.crypto.subtle, plenumClient, {
    authType: 'midway',
    stage: 'prod',
    region,
  });

  const notaryClient = new PiezoNotaryThickClient(plenumClient, {
    authType: 'midway',
    stage: 'prod',
    region,
  });

  const witnessClient = new PiezoWitnessThickClient({
    authType: 'midway',
    stage: 'prod',
    region,
  });

  return { plenumClient, okapiClient, notaryClient, witnessClient };
};

/**
 * Formats a Piezo action string to a tool path by replacing all _ with /
 */
export const formatPiezoActionToToolPath = (piezoAction: string): string => {
  return piezoAction.replaceAll('_', '/');
};

/**
 * Formats a tool path to a Piezo action string by replacing all / with _
 */
export const formatToolPathToPiezoAction = (toolPath: string): string => {
  return toolPath.replaceAll('/', '_');
};

/**
 * Extracts unique notary clerk endpoints from a list of parsed permits
 */
export const getNotaryClerksFromPermits = (permits: PiezoParsedPermitMetadata[]): string[] => {
  return Array.from(new Set(permits.map((p) => p.clerkEndpoint)));
};

/**
 * Returns true if the list of permits passes the unique permits requirement. A policy can be used if it
 * has at least 2 unique permits from different Notary clerks, differentiated by their clerk endpoint.
 */
export const passesUniquePermitRequirement = (permits: PiezoParsedPermitMetadata[]): boolean => {
  return getNotaryClerksFromPermits(permits).length >= PIEZO_MINIMUM_UNIQUE_PERMITS_REQUIREMENT;
};

/**
 * Determines if resources should be refreshed based on the last modified date.
 * Resources should be refreshed if they were last modified more than 3 days ago.
 */
export const PIEZO_RESOURCE_REFRESH_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

export const shouldRefreshResources = (lastModifiedDate: Date): boolean => {
  return Date.now() - new Date(lastModifiedDate).getTime() > PIEZO_RESOURCE_REFRESH_MAX_AGE_MS;
};

/**
 * Generates a cryptographic keypair for the user using the Web Crypto API for use with Okapi identity registration.
 * Key is non-extractable and can only be used for signing.
 */
export const generateKeyPair = (): Promise<CryptoKeyPair> => {
  return crypto.subtle.generateKey(PiezoKeyPairAlgorithm, false, ['sign']);
};

/**
 * Generates a SHA-256 hash for a given byte array
 */
export const generateSHA256HashForBytes = async (
  bytes: Uint8Array,
  encoding: 'base64' | 'hex',
): Promise<string> => {
  const safeBytes = new Uint8Array(bytes);
  const hashBuffer = await crypto.subtle.digest('SHA-256', safeBytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  if (encoding === 'hex') {
    return hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  return btoa(String.fromCharCode.apply(null, hashArray));
};

/**
 * Generates a SHA-256 hash for a given string
 */
export const generateSHA256HashForString = async (input: string, encoding: 'base64' | 'hex'): Promise<string> => {
  const encoder = new TextEncoder();
  return generateSHA256HashForBytes(encoder.encode(input), encoding);
};

/**
 * Generates a hex-encoded SHA-256 hash of a raw policy string to be used as unique identifiers for policies
 */
export const generatePolicyHash = async (rawPolicyString: string | undefined): Promise<string | undefined> => {
  if (!rawPolicyString) {
    return undefined;
  }
  return generateSHA256HashForString(rawPolicyString, 'hex');
};

/**
 * Parses a permit into its metadata components including policy name, hash, permitted tools, and principal
 */
export const parsePermit = async (
  permit: PermitInfo,
  plenumClient: PiezoPlenumClient,
): Promise<PiezoParsedPermitMetadata> => {
  const permitBytes: Uint8Array | undefined = permit.permit.permit;
  if (!permitBytes) {
    throw Error('Error parsing permit: missing permit binary');
  }

  const encodedPermit = bytesToEncoded(permitBytes);
  const permitPlenumDocument: ParsePlenumDocumentCommandOutput = await plenumClient.parsePlenumDocument({
    document: encodedPermit,
  });

  if (!permit.clerkEndpoint?.trim()) {
    throw Error('Error parsing permit: missing clerk endpoint');
  }

  if (!permit.permit.permitId || permit.permit.permitId.trim() === '') {
    throw Error('Error parsing permit: missing permit Id');
  }

  return {
    clerkEndpoint: permit.clerkEndpoint,
    permitId: permit.permit.permitId,
    encodedPermit,
    permitPlenumDocument,
  };
};

export const buildPolicyMetadata = async (
  parsedPermit: PiezoParsedPermitMetadata,
): Promise<PiezoParsedPolicyMetadata> => {
  const permitDocument = parsedPermit.permitPlenumDocument.modeledDocument?.permit;
  if (!permitDocument) {
    throw Error('Error building policy metadata: missing permit modeled document');
  }

  const policyHash: string | undefined = await generatePolicyHash(permitDocument.rawPolicyText);
  if (!policyHash) {
    throw Error(
      `Error generating policy hash for permit ${parsedPermit.permitId} for policy ${permitDocument.policyName}`,
    );
  }

  const policyName: string | undefined = permitDocument.policyName;
  if (!policyName?.trim()) {
    throw Error(`Policy name is missing for permit ${parsedPermit.permitId}`);
  }

  const principal: string | undefined = permitDocument.parsedCedarHeaders?.success?.authorizedPrincipal?.role;
  if (!principal?.trim()) {
    throw Error(`Principal is missing for permit ${parsedPermit.permitId} for policy ${permitDocument.policyName}`);
  }

  const permittedTools: PiezoParsedPolicyMetadata['permittedTools'] =
    permitDocument.parsedCedarHeaders?.success?.authorizedActions?.map((a: string) => formatPiezoActionToToolPath(a)) || [];

  return {
    policyHash,
    policyName,
    principal,
    permittedTools,
    permits: [],
  };
};

/**
 * Builds an index of policies and their associated tools from fetched permits.
 * Filters permits based on unique permit requirements and extracts authorized actions.
 */
export const buildResourceIndexes = async (
  fetchedPermitsMap: PiezoFetchedPermitsMap,
  plenumClient: PiezoPlenumClient,
): Promise<{
  policyIndex: PiezoPolicyIndex;
  toolIndex: PiezoToolIndex;
}> => {
  const policyIndex: PiezoPolicyIndex = {};

  // Permits of one policy all hash to the same key, so each iteration after the first merges into an
  // existing entry — that accumulation is how a policy collects its permits.
  for (const permitList of Array.from(fetchedPermitsMap.values())) {
    for (const fetchedPermit of permitList) {
      const parsedPermitMetadata: PiezoParsedPermitMetadata = await parsePermit(fetchedPermit, plenumClient);
      const policyMetadata: PiezoParsedPolicyMetadata = await buildPolicyMetadata(parsedPermitMetadata);
      const policyIndexKey = policyMetadata.policyHash + '_' + policyMetadata.policyName;

      const oldPolicyIndexEntry: PiezoParsedPolicyMetadata | undefined = policyIndex[policyIndexKey];

      policyIndex[policyIndexKey] = {
        ...policyMetadata,
        permittedTools: Array.from(new Set((oldPolicyIndexEntry?.permittedTools ?? []).concat(policyMetadata.permittedTools))),
        permits: [...(oldPolicyIndexEntry?.permits ?? []), parsedPermitMetadata],
      };
    }
  }

  return filterPolicyIndexAndBuildToolIndex(policyIndex);
};

/**
 * Filters the policy index to only include policies that pass the unique permit requirement (2 permits from 2 different notary clerks)
 * and builds a tool index mapping every tool mentioned in a permit to the policies that mention it.
 */
export const filterPolicyIndexAndBuildToolIndex = (
  policyIndex: PiezoPolicyIndex,
): { policyIndex: PiezoPolicyIndex; toolIndex: PiezoToolIndex } => {
  const filteredPolicyIndex: PiezoPolicyIndex = {};
  const toolIndex: PiezoToolIndex = {};

  for (const [policyIndexKey, policyMetadata] of Object.entries(policyIndex)) {
    const passes = passesUniquePermitRequirement(policyMetadata.permits);
    if (passes) {
      filteredPolicyIndex[policyIndexKey] = policyMetadata;
      policyMetadata.permittedTools.forEach((tool) => {
        const oldToolIndexData: { policies: string[] } | undefined = toolIndex[tool];
        const mergedPolicies = Array.from(new Set((oldToolIndexData?.policies ?? []).concat([policyIndexKey])));
        toolIndex[tool] = { policies: mergedPolicies };
      });
    }
  }

  return { policyIndex: filteredPolicyIndex, toolIndex };
};

export const getDerivedNotaryDecks = (fetchedNotaryDecks: PiezoFetchedNotaryDecks): PiezoNotaryDecks => {
  return Object.fromEntries(fetchedNotaryDecks.entries());
};

/**
 * Pre-encodes requester decks to base64 once (at hydrate time) so the artifact bundle can reuse the
 * strings on every tool call instead of re-running bytesToEncoded. Mirrors getDerivedWitnessDeckbox.
 */
export const getEncodedDecks = (decks: PiezoDecks): string[] => {
  return decks.map((deck) => bytesToEncoded(deck));
};

/**
 * Pre-encodes notary clerk decks to base64 once (at hydrate time), keyed by clerk endpoint, so the
 * artifact bundle can reuse the strings on every tool call instead of re-running bytesToEncoded.
 */
export const getEncodedNotaryDecks = (notaryDecks: PiezoNotaryDecks): PiezoEncodedNotaryDecks => {
  const encoded: PiezoEncodedNotaryDecks = {};
  for (const [endpoint, decks] of Object.entries(notaryDecks)) {
    encoded[endpoint] = decks.map((deck) => bytesToEncoded(deck));
  }
  return encoded;
};

/**
 * Computes a stable SHA-256 (hex) over the raw fetched permits. Used as the parsed-index cache key:
 * the cached policyIndex/toolIndex is valid only while this hash matches (see PIEZO_OPT1). Permits
 * are sorted by permitId and both the permitId and the raw bytes are folded in, so any add/remove/
 * change of permits yields a different hash. Cheap relative to N Plenum WASM parses.
 */
export const hashPermits = async (fetchedPermits: PiezoFetchedPermitsMap): Promise<string> => {
  const permits = Array.from(fetchedPermits.values()).flat();
  const usable = permits.filter((p) => p.permit?.permit && p.permit?.permitId);
  usable.sort((a, b) => {
    const ai = a.permit.permitId as string;
    const bi = b.permit.permitId as string;
    return ai < bi ? -1 : ai > bi ? 1 : 0;
  });

  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  let totalLength = 0;
  for (const p of usable) {
    const idBytes = encoder.encode(`${p.permit.permitId}\u0000`);
    const permitBytes = p.permit.permit as Uint8Array;
    parts.push(idBytes, permitBytes);
    totalLength += idBytes.length + permitBytes.length;
  }

  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    combined.set(part, offset);
    offset += part.length;
  }

  return generateSHA256HashForBytes(combined, 'hex');
};

/**
 * Produces a slimmed copy of a policyIndex for persisting in the parsed-index cache: each permit keeps
 * only the fields the bundle path uses, dropping the heavy parsed permitPlenumDocument. Still a valid
 * PiezoPolicyIndex, since that field is optional.
 */
export const slimPolicyIndexForCache = (policyIndex: PiezoPolicyIndex): PiezoPolicyIndex => {
  const slim: PiezoPolicyIndex = {};
  for (const [key, meta] of Object.entries(policyIndex)) {
    slim[key] = {
      ...meta,
      permits: meta.permits.map((p) => ({
        clerkEndpoint: p.clerkEndpoint,
        permitId: p.permitId,
        encodedPermit: p.encodedPermit,
      })),
    };
  }
  return slim;
};

export const getDerivedWitnessDeckbox = (fetchedWitnessDeckbox: PiezoFetchedWitnessDeckbox): PiezoWitnessDeckbox => {
  const witnessDeckbox: PiezoWitnessDeckbox = {};

  fetchedWitnessDeckbox.forEach(({ decks, region }) => {
    witnessDeckbox[region] = decks.map((d) => bytesToEncoded(d));
  });

  return witnessDeckbox;
};

// ── Piezo Execution Context Utils ──────────────────────────────────────────────────────────────

/**
 * Extracts the key ID from the first deck using the Okapi client and returns it as an encoded string.
 * Any deck can be used.
 */
export const getKeyId = async (decks: PiezoDecks, okapiClient: PiezoOkapiThickClient): Promise<string | undefined> => {
  if (decks.length === 0) {
    throw Error('Failed to get key id from user decks because no decks available');
  }

  const keyId: Uint8Array = await okapiClient.extractKeyId(decks[0]);
  return bytesToEncoded(keyId);
};

/**
 * Runtime shape of a deck principal card. The .d.ts says `Tags`, but the WASM bridge output is a bare
 * JSON.parse (no smithy deserializer), so the runtime key is `tags`. Reading only one spelling resolves
 * zero roles and nothing signs — RDSOpsUI shipped exactly that bug.
 */
type RuntimePrincipalCard = { Tags?: string[]; tags?: string[] };

/** Reads the principal tag list off a parsed deck, tolerating all known key spellings. */
const getDeckPrincipalTags = (parsed: ParsePlenumDocumentCommandOutput): string[] => {
  const principal = parsed?.modeledDocument?.deck?.principal as RuntimePrincipalCard | undefined;
  const tags = principal?.Tags ?? principal?.tags;
  return Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === 'string') : [];
};

/**
 * Extracts the operator's POSIX roles from their decks, trusting a tag only when at least
 * PIEZO_MINIMUM_REQUIRED_ISSUERS DISTINCT decks attest it. Returns `posix=<group>` verbatim, because a
 * policy's `principal` has the identical form. Never throws; total failure yields [] and fails closed.
 */
export const getOperatorRolesFromDecks = async (
  encodedDecks: string[],
  plenumClient: PiezoPlenumClient,
): Promise<PiezoOperatorRoles> => {
  // Each parse is independent and this sits on the hydrate critical path, so they run together rather
  // than one-at-a-time. allSettled preserves input order, so the deck[i] index below stays meaningful.
  const results = await Promise.allSettled(
    encodedDecks.map((document) => plenumClient.parsePlenumDocument({ document })),
  );

  const attestationCounts = new Map<string, number>();
  results.forEach((result, i) => {
    if (result.status === 'rejected') {
      // A single unparseable deck must not blank the whole role set.
      console.warn(
        `${PiezoDebugPrefix} getOperatorRolesFromDecks: skipping unparseable deck[${i}]: ${String(result.reason)}`,
      );
      return;
    }
    const allTags = getDeckPrincipalTags(result.value);
    const posixTags = new Set(allTags.filter((tag) => tag.startsWith(PIEZO_POSIX_TAG_PREFIX)));
    // Array.from is required, not noise: target is es5 without downlevelIteration, so iterating a
    // Set/Map directly is a TS2569 compile error.
    for (const tag of Array.from(posixTags)) {
      attestationCounts.set(tag, (attestationCounts.get(tag) ?? 0) + 1);
    }
  });

  const roles: string[] = [];
  const rejected: string[] = [];
  for (const [tag, count] of Array.from(attestationCounts.entries())) {
    if (count >= PIEZO_MINIMUM_REQUIRED_ISSUERS) {
      roles.push(tag);
    } else {
      rejected.push(`${tag}(${count})`);
    }
  }
  roles.sort(); // deterministic ordering for logs and tests

  return roles;
};

/**
 * ELIGIBILITY (not selection): keeps policies whose principal is one of the operator's own POSIX roles.
 * Needed because permit issuance is ownership-gated, not group-gated, so an operator holds permits for
 * policies whose group they are not in. Exact equality on `principal`; NEVER a glob on the ad-hoc name.
 */
export const filterPoliciesByPrincipal = (input: {
  candidateKeys: string[];
  policyIndex: PiezoPolicyIndex;
  operatorRoles: ReadonlySet<string>;
}): string[] => {
  const { candidateKeys, policyIndex, operatorRoles } = input;
  return candidateKeys.filter((key) => {
    const principal = policyIndex[key]?.principal;
    return !!principal && operatorRoles.has(principal);
  });
};

/**
 * Keeps only the policies whose name ends with this deployment's stage, e.g. `-gamma`.
 *
 * The operator's decks attest every policy they can reach, so a gamma browser is offered beta policies
 * too, and signing under one gets the artifact rejected by piezod.
 *
 * @param candidateKeys - policyIndex keys to filter, in order.
 * @param policyIndex - supplies each key's policyName, which is what carries the stage.
 * @param stage - this deployment's stage. Omitted, nothing is filtered.
 */
export const filterPoliciesByStage = (input: {
  candidateKeys: string[];
  policyIndex: PiezoPolicyIndex;
  stage?: string;
}): string[] => {
  const { candidateKeys, policyIndex, stage } = input;
  if (!stage) {
    return candidateKeys;
  }
  // Lower-cased on both sides: CDK-injected config is not normalized.
  const suffix = `-${stage.toLowerCase()}`;
  return candidateKeys.filter((key) =>
    String(policyIndex[key]?.policyName ?? '')
      .toLowerCase()
      .endsWith(suffix),
  );
};

/** Rank of the operator's POSIX group; unknown groups sort last. */
export const rankPrincipal = (principal: string): number =>
  PIEZO_PRINCIPAL_POWER_RANK[principal] ?? PIEZO_UNKNOWN_PRINCIPAL_RANK;

/**
 * Privilege tier of a policy: its position in PIEZO_POLICY_TIERS, or the default if it matches none.
 * findIndex returns the first LIST position, so a name carrying more than one tier word takes the lower
 * (more conservative) tier whatever order the words appear in. Affects ORDERING only.
 */
export const rankPolicyTier = (policyName: string): number => {
  // String(): strictNullChecks is off, so undefined reaches here (resolvePolicyForTool passes
  // `policyIndex[key]?.policyName` for a key with no entry) and .toLowerCase() would throw.
  const name = String(policyName ?? '').toLowerCase();
  const tier = PIEZO_POLICY_TIERS.findIndex((word) => name.includes(word.toLowerCase()));
  return tier === -1 ? PIEZO_DEFAULT_POLICY_TIER : tier;
};

/**
 * Resolves a tool to one policy index key, or a typed failure: keep only policies whose principal is one
 * of the operator's roles, then take the first by rank (group power, tier, lexicographic). Sort before
 * picking — Set-derived lists could otherwise resolve differently per session. Non-ok means DO NOT sign.
 */
export const resolvePolicyForTool = (input: {
  toolId: string;
  toolIndex: PiezoToolIndex;
  policyIndex: PiezoPolicyIndex;
  operatorRoles: ReadonlySet<string>;
  stage?: string;
}): PiezoPolicyResolution => {
  const { toolId, toolIndex, policyIndex, operatorRoles, stage } = input;

  const candidateKeys = toolIndex[toolId]?.policies ?? [];
  if (candidateKeys.length === 0) {
    return { ok: false, failure: 'TOOL_NOT_FOUND', candidates: [] };
  }

  const inStage = filterPoliciesByStage({ candidateKeys, policyIndex, stage });
  const eligible = filterPoliciesByPrincipal({
    candidateKeys: inStage,
    policyIndex,
    operatorRoles,
  });
  if (eligible.length === 0) {
    console.warn(
      `${PiezoDebugPrefix} resolvePolicyForTool: no eligible policy for "${toolId}" (stage=${stage}, ` +
        `operatorRoles=${JSON.stringify(Array.from(operatorRoles))}). candidates=` +
        JSON.stringify(
          candidateKeys.map((key) => ({ policyName: policyIndex[key]?.policyName, principal: policyIndex[key]?.principal })),
        ),
    );
    return { ok: false, failure: 'NO_ELIGIBLE_POLICY', candidates: candidateKeys };
  }

  const policyKey = [...eligible].sort((a, b) => {
    const pa = policyIndex[a];
    const pb = policyIndex[b];
    return (
      rankPrincipal(pa.principal) - rankPrincipal(pb.principal) ||
      rankPolicyTier(pa.policyName) - rankPolicyTier(pb.policyName) ||
      a.localeCompare(b)
    );
  })[0];
  return { ok: true, policyKey, policyName: policyIndex[policyKey].policyName };
};

/**
 * Extracts the principal roles associated with a selected policy
 */
export const getRoles = (selectedPolicyMetadata: PiezoParsedPolicyMetadata): string[] => {
  return [selectedPolicyMetadata.principal];
};

// ── Bundle trimming helpers ───────────────────────────────────────────

/**
 * Derives the clerk family from a clerk endpoint, or undefined when no known family appears in it.
 * Endpoints look like midway-prod-us-east-1-{alps,cascades}-{a,b}.
 *
 * Matches family NAMES rather than a positional dash segment, because endpoints with no family suffix
 * exist (bare `us-east-2`, `eu-west-3`) and a positional parse would return garbage like "east".
 */
export const getClerkFamily = (clerkEndpoint: string): string | undefined => {
  // Explicit checks, not truthiness: strictNullChecks is off, so a null/undefined endpoint reaches here.
  if (clerkEndpoint === undefined || clerkEndpoint === null || typeof clerkEndpoint !== 'string') {
    return undefined;
  }
  const haystack = clerkEndpoint.toLowerCase();
  for (const family of PIEZO_CLERK_FAMILIES) {
    // Bounded by dashes so a region or host that merely contains the letters cannot false-positive.
    if (haystack.includes(`-${family}-`) || haystack.endsWith(`-${family}`)) {
      return family;
    }
  }
  return undefined;
};

/**
 * Picks the smallest set of permits worth embedding in the bundle: asks the WASM which permits are
 * valid for this action, then keeps ONE per clerk family (see getClerkFamily). Within a family the
 * choice is by clerkEndpoint then permitId, so it is stable across sessions.
 *
 * Anomalies split two ways.
 *
 * TEMPORARY FAULTS fail open, returning every permit: filterPermits threw, or matched nothing.
 * Neither implicates clerk topology, and the full set is the pre-trim behaviour known to verify —
 * killing every signed call on the page over a blip is the worse trade.
 *
 * CONTRACT VIOLATIONS throw: a survivor whose family cannot be derived, or a family with no survivors.
 * Failing open there would still verify, so the breakage would be invisible while the bundle crept back
 * to ~2.3M chars and resurfaced as a payload-size error far from Piezo. Costs one unsigned tool call,
 * which the caller's existing catch already contains.
 */
export const selectPermitsForBundle = async (input: {
  actionInfo: string;
  permits: PiezoParsedPermitMetadata[];
  plenumClient: PiezoPlenumClient;
}): Promise<PiezoParsedPermitMetadata[]> => {
  const { actionInfo, permits, plenumClient } = input;
  const failOpen = (reason: string): PiezoParsedPermitMetadata[] => {
    return permits;
  };
  const failClosed = (reason: string): never => {
    throw Error(
      `${PiezoDebugPrefix} clerk-family contract violated: ${reason}. ` +
        `Refusing to fall back to all ${permits.length} permits — that would silently restore ` +
        `the ~2.3M-char bundle. Expected families: ${JSON.stringify(PIEZO_CLERK_FAMILIES)}.`,
    );
  };

  if (permits.length <= PIEZO_TRIM_PERMITS_PER_POLICY) {
    return permits;
  }

  // Only the WASM call can fault, so only it is wrapped: a wider try would also catch failClosed below
  // and convert a contract breach into a silent fail-open.
  let permitsByPolicy: Record<string, string[] | undefined> | undefined;
  try {
    ({ permitsByPolicy } = await plenumClient.filterPermits({
      actionInfoDocument: actionInfo,
      permits: permits.map((p) => p.encodedPermit),
      filterMode: 'HEADER_AND_APPROVAL_FILTERING',
    }));
  } catch (e) {
    return failOpen(`filterPermits threw: ${e instanceof Error ? e.message : String(e)}`);
  }

  const surviving = new Set(Object.values(permitsByPolicy ?? {}).flatMap((list) => list ?? []));
  if (surviving.size === 0) {
    return failOpen('filterPermits returned no matching permits');
  }

  // Lowest clerkEndpoint then permitId wins its family, so the choice is stable across sessions.
  const candidates = permits
    .filter((p) => surviving.has(p.encodedPermit))
    .sort((a, b) => a.clerkEndpoint.localeCompare(b.clerkEndpoint) || a.permitId.localeCompare(b.permitId));

  // An underivable family means PIEZO_CLERK_FAMILIES is stale or the endpoint shape changed. Guessing
  // risks shipping a single-family set that piezod denies, so break instead.
  for (const p of candidates) {
    if (getClerkFamily(p.clerkEndpoint) === undefined) {
      failClosed(`survivor ${p.permitId} has unknown clerk family (endpoint=${p.clerkEndpoint})`);
    }
  }

  // One per family. No separate distinct-clerk check is needed: an endpoint belongs to exactly one
  // family, so one-per-family cannot pick the same clerk twice.
  return PIEZO_CLERK_FAMILIES.map((family) => {
    const pick = candidates.find((p) => getClerkFamily(p.clerkEndpoint) === family);
    if (pick === undefined) {
      failClosed(`no surviving permit for clerk family "${family}"`);
    }
    return pick;
  });
};

/**
 * Builds server identifier strings for a given cluster, namespace, stack domain, and partition
 */
export const getServerIdentifiers = (
  clusterId: string,
  namespaceId: string,
  stackDomain: string,
  partition: string,
): string[] => {
  if (!clusterId.trim()) {
    throw Error('Cluster ID cannot be an empty string');
  }
  if (!namespaceId.trim()) {
    throw Error('Namespace ID cannot be an empty string');
  }
  if (!stackDomain.trim()) {
    throw Error('Stack domain cannot be an empty string');
  }
  if (!partition.trim()) {
    throw Error('Partition cannot be an empty string');
  }

  return [
    `partition=${partition}`,
    `mechanicNamespaceClusterId=${stackDomain}:${namespaceId}:${clusterId}`,
  ];
};

/**
 * Converts a tool path to a Piezo action name by replacing slashes with underscores
 */
export const getActionName = (toolPath: string): string => {
  if (!toolPath.trim()) {
    throw Error('Selected tool path cannot be an empty string');
  }
  return formatToolPathToPiezoAction(toolPath);
};

/**
 * Builds the action arguments array for a Piezo action info document.
 *
 * Simplified from Mechanic Console: OpsTumbler doesn't have file parameters or external approval
 * metadata, so we just hash the payload string directly.
 */
/**
 * Hex-encoded SHA-256 of the canonicalized (RFC-8785 / JCS) parameter payload, matching how
 * MPA recomputes it. MPA canonicalizes { Parameters, InputFileParameterMetadata } — OpsTumbler
 * has no file parameters, so InputFileParameterMetadata is always {}. The payloadString must be
 * the JSON string of { parameters: {...} }.
 */
export const getCanonicalizedParameterPayloadHash = async (payloadString: string): Promise<string> => {
  const canonicalizedObj = canonicalize({
    Parameters: JSON.parse(payloadString).parameters,
    InputFileParameterMetadata: {},
  });
  if (!canonicalizedObj) {
    throw Error('Error canonicalizing parameter payload');
  }
  return generateSHA256HashForString(canonicalizedObj, 'hex');
};

export const getActionArguments = async (payloadString: string): Promise<string[]> => {
  const canonicalizationVersionNumber = '1';
  const parameterPayloadHash: string = await getCanonicalizedParameterPayloadHash(payloadString);
  // No external approval target resource info for OpsTumbler
  const targetResourceInfo = '{}';

  return [canonicalizationVersionNumber, parameterPayloadHash, targetResourceInfo];
};

/**
 * Creates an action info document for a Piezo execution by assembling key ID, roles, server identifiers,
 * action name, and action arguments, then signing them into a Plenum action info document.
 */
export const createActionInfoDoc = async ({
  inputs,
  resolverContext,
}: PiezoCreateActionInfoDocInput): Promise<string> => {
  const selectedPolicyMetadata: PiezoParsedPolicyMetadata | undefined = resolverContext.policyIndex[inputs.policyHash];
  if (!selectedPolicyMetadata) {
    throw Error(`Selected policy with hash ${inputs.policyHash} does not exist`);
  }

  const keyId: string | undefined = await getKeyId(resolverContext.decks, resolverContext.okapiClient);
  if (!keyId) {
    throw Error('Failed to get key Id from user decks');
  }

  const roles: string[] = getRoles(selectedPolicyMetadata);
  if (roles.length === 0) {
    throw Error(`No principal roles found for policy ${selectedPolicyMetadata.policyName}`);
  }

  const serverIdentifiers: string[] = getServerIdentifiers(
    inputs.cluster.id,
    inputs.namespaceId,
    inputs.stackDomain,
    inputs.partition,
  );

  const actionName = getActionName(inputs.toolId);

  const actionArguments = await getActionArguments(inputs.payloadString);
  const executionTime = new Date().toISOString();

  // Create Action Info Doc with Plenum client (base64 encoded Plenum document bytes)
  const { actionInfoDocument } = await resolverContext.plenumClient.createActionDocuments(
    {
      keyId,
      keypair: createInlineKeyPair(),
      roles,
      serverIdentifiers,
      actionName,
      actionArguments,
      executionTime,
    },
    resolverContext.keyProvider.getPrivateCryptoKey(),
  );

  if (!actionInfoDocument) {
    throw Error('Error calling plenumClient.createActionDocuments');
  }

  return actionInfoDocument;
};

/**
 * Creates a Piezo artifact bundle containing the action info document, keypair, decks, and permits
 * formatted as a base64-encoded string.
 */
export const createArtifactBundle = async ({
  actionInfo,
  additionalDecks,
  observationDocuments = [],
  selectedPermits,
  inputs,
  resolverContext,
}: PiezoCreateArtifactBundleInput): Promise<string> => {
  // Only permits are trimmed. additionalDecks is passed through unfiltered: createPiezoRequest picks
  // the decks it needs itself, and filtering them here measured larger.
  const permits = (selectedPermits ?? resolverContext.policyIndex[inputs.policyHash].permits)
    .map((permit) => permit.encodedPermit)
    .filter((p) => p !== '');
  const { piezoRequest } = await resolverContext.plenumClient.createPiezoRequest(
    {
      keypair: createInlineKeyPair(),
      requesterDecks: resolverContext.encodedDecks,
      additionalDecks,
      permits,
      actionInfo,
      observationDocuments,
      piezoProtocolVersion: PiezoProtocolVersion,
    },
    resolverContext.keyProvider.getPrivateCryptoKey(),
  );

  if (!piezoRequest) {
    throw Error('Failed to create Piezo request');
  }

  return piezoRequest;
};

// ── Indexed DB Utils ──────────────────────────────────────────────────────────────

/**
 * Opens Piezo IndexedDB database
 */
export const openPiezoDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const { db, table } = PiezoIndexedDBMetadata;

    const request = indexedDB.open(db.name, db.version);

    request.onerror = () => {
      reject('Failed to open Piezo IndexedDB database');
    };

    // `blocked` fires when a version upgrade is held off by another connection, and then nothing else
    // fires — no success, no error — so without this the promise never settles. Reject rather than
    // resolve undefined: callers treat a failed open as "no cache", but undefined looks like an empty one.
    request.onblocked = () => {
      const message = 'IndexedDB open blocked by another connection (another tab?)';
      console.warn(`${PiezoDebugPrefix} ${message}`);
      reject(new Error(message));
    };

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(table.name)) {
        database.createObjectStore(table.name, { keyPath: table.key });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
};

/**
 * Stores Piezo resources in Piezo IndexedDB
 */
export const cachePiezoResources = (db: IDBDatabase, resources: PiezoIndexedDBSchema): Promise<void> => {
  return new Promise((resolve, reject) => {
    const { table } = PiezoIndexedDBMetadata;
    const transaction = db.transaction([table.name], 'readwrite');
    const objectStore = transaction.objectStore(table.name);
    const request = objectStore.put({
      ...resources,
      id: table.keyValue,
    });

    request.onerror = () => {
      const errorMessage = `Failed to cache Piezo resources in IndexedDB: ${request.error}`;
      console.error(errorMessage);
      reject(errorMessage);
    };

    request.onsuccess = () => {
      resolve();
    };
  });
};

/**
 * Gets Piezo resources stored in Piezo IndexedDB
 */
export const loadPiezoResources = (db: IDBDatabase): Promise<PiezoIndexedDBSchema | undefined> => {
  return new Promise((resolve, reject) => {
    const { table } = PiezoIndexedDBMetadata;
    const transaction = db.transaction([table.name]);
    const objectStore = transaction.objectStore(table.name);
    const request = objectStore.get(table.keyValue);

    request.onerror = () => {
      const errorMessage = `Error loading Piezo resources from IndexedDB: ${request.error}`;
      console.error(errorMessage);
      reject(errorMessage);
    };

    request.onsuccess = () => {
      if (!request.result) {
        resolve(undefined);
        return;
      }
      resolve(request.result);
    };
  });
};
